import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, Clock, Plus } from 'lucide-react';
import PageHero from '../components/PageHero';
import apiClient from '../utils/apiClient';
import { getTaskTitle, getTaskFormat, formatTimeAgo } from '../utils/statsUtils';
import { compactToolDetail, formatToolActivityText } from '../utils/toolActivity';

const STEP_AGENTS = {
  1: { name: 'Content Strategist', initials: 'CS', action: 'planning strategy' },
  2: { name: 'Research Writer', initials: 'RW', action: 'gathering research' },
  3: { name: 'Research Writer', initials: 'RW', action: 'developing outline' },
  4: { name: 'Research Writer', initials: 'RW', action: 'writing first draft' },
  5: { name: 'Fact-Checker / Verification Specialist', initials: 'FC', action: 'fact-checking content' },
  6: { name: 'Managing Editor', initials: 'ME', action: 'editing and refining' },
  7: { name: 'Content Strategist', initials: 'CS', action: 'approving final draft' },
  8: { name: 'SEO Specialist & Copywriter', initials: 'SE', action: 'optimising for publication' },
  9: { name: 'Content Strategist', initials: 'CS', action: 'monitoring performance' },
};

const STEP_NAMES = {
  '1': 'Topic Selection & Strategy Planning',
  '2': 'Research & Information Gathering',
  '3': 'Outline & Structure Development',
  '4': 'First Draft Creation',
  '5': 'Fact-Checking & Verification',
  '6': 'Multi-Layered Editing',
  '7': 'Final Approval & Strategy Check',
  '8': 'Publication & Distribution',
  '9': 'Post-Publication Monitoring & Updates',
};

function buildFeedFromInstances(instances) {
  const feed = [];

  instances.forEach(inst => {
    const title = getTaskTitle(inst);
    const format = getTaskFormat(inst);
    const rawTag = title.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 30).trim();
    const projectTag = '#' + rawTag.replace(/\s+/g, '');

    if (inst.status === 'completed') {
      const stepCount = Object.keys(inst.completed_steps || {}).length;
      feed.push({
        id: `completed-${inst.id}`,
        type: 'system',
        initials: '✓',
        name: 'System',
        action: 'workflow completed',
        project: projectTag,
        message: `${format} completed — ${stepCount} step${stepCount !== 1 ? 's' : ''} executed successfully.`,
        time: formatTimeAgo(inst.updated_at || inst.created_at),
        likes: 0,
      });
    }

    if (inst.status === 'in_progress') {
      const doneCount = Object.keys(inst.completed_steps || {}).length;
      const currentStep = doneCount + 1;
      const agent = STEP_AGENTS[currentStep] || { name: 'AI Team', initials: 'AI', action: 'processing' };
      feed.push({
        id: `inprogress-${inst.id}`,
        type: 'agent',
        initials: agent.initials,
        name: agent.name,
        action: agent.action,
        project: projectTag,
        message: `Step ${currentStep} of 9 — ${agent.name} is currently ${agent.action} for "${title.substring(0, 50)}".`,
        time: formatTimeAgo(inst.created_at),
        likes: 0,
      });
    }

    const completedEntries = Object.entries(inst.completed_steps || {}).slice(-2);
    completedEntries.forEach(([stepNum, stepName]) => {
      feed.push({
        id: `step-${inst.id}-${stepNum}`,
        type: 'system',
        initials: '✓',
        name: 'System',
        action: 'step completed',
        project: projectTag,
        message: `Step ${stepNum}: ${STEP_NAMES[stepNum] || stepName} — complete.`,
        time: formatTimeAgo(inst.updated_at || inst.created_at),
        likes: 0,
      });
    });
  });

  return feed;
}

function getAgentInitials(name) {
  const map = {
    'Content Strategist': 'CS',
    'Research Writer': 'RW',
    'Managing Editor': 'ME',
    'SEO Specialist & Copywriter': 'SE',
    'Fact-Checker / Verification Specialist': 'FC',
    'Philosopher / Thought Leader': 'TL',
    'Subject Matter Expert (SME)': 'SM',
  };
  if (map[name]) return map[name];
  const words = name.split(' ');
  return ((words[0]?.[0] || '') + (words[1]?.[0] || '')).toUpperCase();
}

function getAvatarBg(type) {
  if (type === 'system') return '#F97316';
  if (type === 'user') return '#1E3A8A';
  return '#1E3A8A';
}

export default function TheGreenhouse() {
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(8);
  const wsRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    apiClient.get('/workflow-instances/')
      .then(r => {
        const list = r.data?.results || r.data || [];
        setInstances(list);
        setFeed(buildFeedFromInstances(list));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const WS_BASE = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
    const ws = new WebSocket(`${WS_BASE}/ws/socket-server/`);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        const msgType = data.type || '';

        let name = 'System';
        let initials = 'SY';
        let type = 'system';
        let action = 'update';
        let message = '';

        if (msgType === 'workflow.step_started') {
          name = data.lead_agent || 'Agent';
          initials = getAgentInitials(name);
          type = 'agent';
          action = 'started step';
          message = `Starting Step ${data.step_order}: ${data.step_name}`;
        } else if (msgType === 'workflow.step_completed') {
          name = data.lead_agent || 'System';
          initials = getAgentInitials(name);
          type = 'system';
          action = 'step completed';
          message = `✓ Completed Step ${data.step_order}: ${data.step_name}`;
        } else if (msgType === 'workflow.completed') {
          name = 'System';
          initials = '✓';
          type = 'system';
          action = 'workflow completed';
          message = '🎉 Workflow completed! Content is ready.';
        } else if (msgType === 'agent.output' || msgType === 'agent.update') {
          name = data.agent || 'Agent';
          initials = getAgentInitials(name);
          type = 'agent';
          action = 'produced output';
          message = (data.message || data.output || 'Working...').substring(0, 200);
        } else if (msgType === 'tool.used') {
          name = data.agent || 'Agent';
          initials = getAgentInitials(name);
          type = 'agent';
          action = formatToolActivityText(data);
          const detail = compactToolDetail(data);
          message = detail
            ? `Step ${data.step_order || '?'} — ${detail}`
            : `Step ${data.step_order || '?'} — live tool activity`;
        } else {
          return;
        }

        setFeed(prev => [{
          id: Date.now() + Math.random(),
          type,
          initials,
          name,
          action,
          project: `#Workflow ${data.workflow_id || ''}`,
          message,
          time: 'just now',
          likes: 0,
        }, ...prev].slice(0, 50));
      } catch (err) {}
    };
    wsRef.current = ws;
    return () => ws.close();
  }, []);

  const activeCount = instances.filter(i => i.status === 'in_progress').length;

  const page = {
    maxWidth: 680,
    margin: '0 auto',
    padding: isMobile ? '20px 16px 24px' : '24px',
    fontFamily: 'Inter, sans-serif',
    width: '100%',
    minWidth: 0,
  };
  const card = {
    background: '#1A2236',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: isMobile ? 16 : 20,
    marginBottom: 16,
  };

  const visibleFeed = isMobile ? feed.slice(0, mobileVisibleCount) : feed;

  return (
    <div style={page}>
      <PageHero
        badge="COLLABORATION"
        title="The Greenhouse"
        subtitle="Watch your content grow. A live feed of every iteration, comment, and breakthrough from your AI team."
      />

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: activeCount > 0 ? '#F97316' : '#4B5563',
          }} />
          <span style={{ fontSize: 14, color: '#CBD5E1', fontWeight: 500 }}>
            {activeCount > 0
              ? `Live — agents active across ${activeCount} project${activeCount !== 1 ? 's' : ''}`
              : 'No active projects right now'}
          </span>
        </div>
        <span style={{ fontSize: 12, color: '#6B7280' }}>
          Live events will appear as your agents work.
        </span>
      </div>

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: '#6B7280', padding: 40 }}>Loading...</div>
      ) : feed.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 40 }}>
          <div style={{ color: '#9CA3AF', fontSize: 15, marginBottom: 16 }}>
            No activity yet. Create your first project to see your AI team in action.
          </div>
          <button
            onClick={() => navigate('/task-creation')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 20px', background: '#F97316', color: 'white',
              border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <Plus size={16} />
            Create Project
          </button>
        </div>
      ) : visibleFeed.map(item => (
        <div key={item.id} style={card}>
          <div style={{ display: 'flex', gap: isMobile ? 10 : 12, minWidth: 0 }}>
            <div style={{
              width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 12, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: isMobile ? 12 : 13, fontWeight: 700,
              background: getAvatarBg(item.type),
            }}>{item.initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#F1F5F9' }}>{item.name}</span>
                {' '}
                <span style={{ color: '#6B7280', fontSize: 13 }}>{item.action}</span>
              </div>
              <div style={{ color: '#F97316', fontSize: 13, fontWeight: 600, marginBottom: 8, overflowWrap: 'anywhere' }}>{item.project}</div>
              <p style={{ margin: 0, fontSize: isMobile ? 13 : 14, color: '#CBD5E1', lineHeight: 1.6, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{item.message}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6B7280', fontSize: 12 }}>
                  <Clock size={12} />
                  {item.time}
                </div>
                {item.likes > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6B7280', fontSize: 12 }}>
                    <ThumbsUp size={12} />
                    {item.likes}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {isMobile && feed.length > mobileVisibleCount && (
        <button
          onClick={() => setMobileVisibleCount((count) => count + 8)}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            color: '#CBD5E1',
            fontWeight: 700,
            cursor: 'pointer',
            marginBottom: 8,
          }}
        >
          Show 8 more updates
        </button>
      )}
    </div>
  );
}
